'use client'

/**
 * Social Login Buttons Component
 *
 * Renders Google and Apple sign-in buttons with proper styling and icons.
 */

import { useState, useEffect, useRef, type MouseEvent } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'
import { trackEvent, type EventProperties } from '@/lib/analytics'
import { getLastProvider, type RememberedProvider } from '@/lib/auth/last-provider'
import { closeNavigationTarget, openModifiedClickTarget } from '@/lib/navigation/modified-click'
import {
  beginSavedPlanFlow,
  savedPlanEventProperties,
  type SavedPlanAnalyticsContext,
} from '@/lib/saved-plan-analytics'

type OAuthProvider = 'google' | 'apple'

interface SocialLoginButtonsProps {
  redirectTo?: string
  onBeforeRedirect?: (provider: OAuthProvider) => EventProperties | void | Promise<EventProperties | void>
  savedPlanContext?: SavedPlanAnalyticsContext
  onError?: (error: Error) => void
  className?: string
  /** Side-by-side Google + Apple with short labels, for tighter cards. */
  compact?: boolean
  /** Override button labels (full-width mode only), e.g. "Save my plan with Google". */
  googleLabel?: string
  appleLabel?: string
  /** Solid black Apple button (Apple HIG style) instead of the outline default. */
  appleSolid?: boolean
  /** Fully rounded pill buttons (full-width mode only). */
  pill?: boolean
  /** Render a single provider. Lets a one-button surface reuse this component's
   *  OAuth path (saved-plan flow, oauth_initiated, modified-click) rather than
   *  hand-rolling a second one. */
  only?: OAuthProvider
}

export function SocialLoginButtons({
  redirectTo,
  onBeforeRedirect,
  savedPlanContext,
  onError,
  className = '',
  compact = false,
  googleLabel,
  appleLabel,
  appleSolid = false,
  pill = false,
  only,
}: SocialLoginButtonsProps) {
  const { signInWithGoogle, signInWithApple } = useAuth()
  const [loadingGoogle, setLoadingGoogle] = useState(false)
  const [loadingApple, setLoadingApple] = useState(false)
  const [lastProvider, setLastProvider] = useState<RememberedProvider | null>(null)
  const googleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const appleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Read the remembered provider after mount so the returning-user hint
  // doesn't cause an SSR/CSR markup mismatch. The hint is a microcopy line
  // below the buttons (an in-button badge overlapped the label on mobile);
  // compact mode additionally rings the remembered button.
  useEffect(() => {
    setLastProvider(getLastProvider())
  }, [])

  // Reset loading states when user returns to the tab (e.g. after closing OAuth popup)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setLoadingGoogle(false)
        setLoadingApple(false)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (googleTimeoutRef.current) clearTimeout(googleTimeoutRef.current)
      if (appleTimeoutRef.current) clearTimeout(appleTimeoutRef.current)
    }
  }, [])

  const prepareOAuthAnalytics = async (provider: OAuthProvider): Promise<EventProperties> => {
    const callbackProperties = (await onBeforeRedirect?.(provider)) ?? {}
    if (!savedPlanContext) return callbackProperties

    const flow = beginSavedPlanFlow(savedPlanContext, provider)
    const flowProperties = savedPlanEventProperties(flow, {
      save_flow_id: flow.saveFlowId,
      method: flow.method,
      provider: flow.provider,
    })
    trackEvent('saved_plan_started', flowProperties)
    return { ...callbackProperties, ...flowProperties }
  }

  const handleGoogleSignIn = async (event: MouseEvent<HTMLButtonElement>) => {
    const navigationTarget = openModifiedClickTarget(event)
    setLoadingGoogle(true)
    // Safety timeout: reset loading after 10s if redirect doesn't happen
    googleTimeoutRef.current = setTimeout(() => {
      setLoadingGoogle(false)
    }, 10000)

    try {
      const eventProperties = await prepareOAuthAnalytics('google')
      // Track OAuth initiation before redirect
      trackEvent('oauth_initiated', { provider: 'google', ...eventProperties })
      if (navigationTarget) await signInWithGoogle(redirectTo, navigationTarget)
      else await signInWithGoogle(redirectTo)
      if (navigationTarget) {
        if (googleTimeoutRef.current) clearTimeout(googleTimeoutRef.current)
        setLoadingGoogle(false)
      }
    } catch (error) {
      closeNavigationTarget(navigationTarget)
      if (googleTimeoutRef.current) clearTimeout(googleTimeoutRef.current)
      onError?.(error as Error)
      setLoadingGoogle(false)
    }
  }

  const handleAppleSignIn = async (event: MouseEvent<HTMLButtonElement>) => {
    const navigationTarget = openModifiedClickTarget(event)
    setLoadingApple(true)
    // Safety timeout: reset loading after 10s if redirect doesn't happen
    appleTimeoutRef.current = setTimeout(() => {
      setLoadingApple(false)
    }, 10000)

    try {
      const eventProperties = await prepareOAuthAnalytics('apple')
      // Track OAuth initiation before redirect
      trackEvent('oauth_initiated', { provider: 'apple', ...eventProperties })
      if (navigationTarget) await signInWithApple(redirectTo, navigationTarget)
      else await signInWithApple(redirectTo)
      if (navigationTarget) {
        if (appleTimeoutRef.current) clearTimeout(appleTimeoutRef.current)
        setLoadingApple(false)
      }
    } catch (error) {
      closeNavigationTarget(navigationTarget)
      if (appleTimeoutRef.current) clearTimeout(appleTimeoutRef.current)
      onError?.(error as Error)
      setLoadingApple(false)
    }
  }

  // A single-provider render never takes the two-column compact grid — the lone
  // button would sit at half width.
  const wrapperClass = compact && !only
    ? `grid grid-cols-2 gap-2.5 ${className}`
    : `space-y-3 ${className}`
  const buttonClass = compact
    ? 'relative w-full h-11 text-sm font-semibold border-2 hover:bg-gray-50 dark:hover:bg-gray-800 px-3'
    : `relative w-full h-12 text-base font-medium border-2 hover:bg-gray-50 dark:hover:bg-gray-800${pill ? ' rounded-full h-[52px] text-[15px] font-semibold' : ''}`
  const iconClass = compact ? 'mr-1.5 h-4 w-4' : 'mr-2 h-5 w-5'
  const compactRing = 'ring-2 ring-green-300 border-green-300'
  // The remembered-provider hint is only useful next to the button it names.
  const hintProvider = only && only !== lastProvider ? null : lastProvider
  const providerLabel = hintProvider === 'google' ? 'Google' : hintProvider === 'apple' ? 'Apple' : null

  return (
    <div className={wrapperClass}>
      {/* Google Sign In */}
      {only !== 'apple' && (
        <Button
          variant="outline"
          className={`${buttonClass}${compact && lastProvider === 'google' ? ` ${compactRing}` : ''}`}
          onClick={handleGoogleSignIn}
          disabled={loadingGoogle || loadingApple}
        >
          {loadingGoogle ? (
            <Loader2 className={`${iconClass} animate-spin`} />
          ) : (
            <svg className={iconClass} viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
          )}
          {compact ? 'Google' : (googleLabel ?? 'Continue with Google')}
        </Button>
      )}

      {/* Apple Sign In */}
      {only !== 'google' && (
        <Button
          variant="outline"
          className={`${buttonClass}${compact && lastProvider === 'apple' ? ` ${compactRing}` : ''}${
            appleSolid && !compact
              ? ' bg-gray-900 border-gray-900 text-white hover:bg-gray-800 hover:text-white dark:hover:bg-gray-800'
              : ''
          }`}
          onClick={handleAppleSignIn}
          disabled={loadingGoogle || loadingApple}
        >
          {loadingApple ? (
            <Loader2 className={`${iconClass} animate-spin`} />
          ) : (
            <svg className={iconClass} viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08M12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25" />
            </svg>
          )}
          {compact ? 'Apple' : (appleLabel ?? 'Continue with Apple')}
        </Button>
      )}
      {providerLabel && (
        <p className="col-span-2 text-[11px] text-gray-500 text-center leading-none -mt-0.5">
          You last signed in with {providerLabel}
        </p>
      )}
    </div>
  )
}
